import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { MapPin, Edit2, ArrowLeft, Settings as SettingsIcon, Home, Building2, Heart, Camera, Trash2, Upload, Globe, Rocket, CheckCircle, AlertTriangle, LogOut, X, Check, Lock, BookmarkPlus, Ban, Briefcase, NotebookText } from 'lucide-react';
import { Loader } from './ui/Loader';
import { LinkifiedText } from './ui/LinkifiedText';
import { projectId, API_BASE } from '../utils/supabase/info';
import { NORWEGIAN_COUNTIES } from '../utils/norwegian-locations';
import { toast } from '../utils/toast';
import { devLog } from '../utils/logger';
import { canCreateGig, canShowGigInterest, canUseFavorites, canUseSavedSearches } from '../utils/accessPolicy';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { containsProfanity } from '../utils/profanity-filter';
import { getCachedProfile, setCachedProfile } from '../utils/profileCache';
import { compressImageForUpload, PROFILE_SHOWCASE_PRESET } from '../utils/imageCompression';
import { PageShell } from './layout/PageShell';
import { ConfirmDialog } from './ConfirmDialog';
import { useDirtyFormGuard } from '../utils/hooks/useDirtyFormGuard';
import { useFormDraftPersistence } from '../utils/hooks/useFormDraftPersistence';
import { useBeforeUnloadDirty } from '../utils/hooks/useBeforeUnloadDirty';
import { useImageCropModal } from '../utils/hooks/useImageCropModal';
import { AVATAR_IMAGE_SPEC } from '../utils/media/imageCrop';


const SETTINGS_SHEET_BODY_CLASS = 'talentfunn-settings-sheet-open';

interface EmployerProfileViewProps {
  userId: string;
  accessToken: string | null;
  isOwnProfile: boolean;
  currentUserProfile?: any;
  /** When true (desktop), edit drawer is portaled to body so profile is visible behind */
  isDesktop?: boolean;
  onViewFavorites?: () => void;
  onViewSavedSearches?: () => void;
  onSignOut?: () => void;
  onNavigate?: (view: string) => void;
  onCreateGig?: () => void; // Callback to navigate to gigs page and open create gig modal
  onProfileUpdate?: () => void | Promise<void>;
}

export function EmployerProfileView({ userId, accessToken, isOwnProfile, currentUserProfile, isDesktop = false, onViewFavorites, onViewSavedSearches, onSignOut, onNavigate, onCreateGig, onProfileUpdate }: EmployerProfileViewProps) {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [showProfileImageOptions, setShowProfileImageOptions] = useState(false);
  const [showWelcomeSplash, setShowWelcomeSplash] = useState(false);
  const [showSettingsSheet, setShowSettingsSheet] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [showDiscardEditConfirm, setShowDiscardEditConfirm] = useState(false);
  const [showBlockedUsers, setShowBlockedUsers] = useState(false); // State for blocked users modal
  const [blockedUsers, setBlockedUsers] = useState<any[]>([]); // List of blocked users
  const [loadingBlockedUsers, setLoadingBlockedUsers] = useState(false); // Loading state for blocked users
  const [savingProfile, setSavingProfile] = useState(false);
  const [unblockingUserId, setUnblockingUserId] = useState<string | null>(null);
  const [hasPublishedGigs, setHasPublishedGigs] = useState<boolean | null>(null); // null = not loaded, true/false after fetch
  const { openCrop, cropModal } = useImageCropModal();

  useModalFocus(showBlockedUsers || showWelcomeSplash || showEditDrawer, { fullscreen: true });

  const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
  const tfMode = typeof document !== 'undefined' ? document.documentElement.getAttribute('data-tf-mode') : null;
  const isAndroidPwa = tfMode === 'android-pwa';
  const isAndroid = typeof document !== 'undefined' && typeof navigator !== 'undefined' && !document.documentElement.classList.contains('ios-pwa') && (document.documentElement.classList.contains('android-pwa') || /android/i.test(navigator.userAgent));
  const androidHeaderPadTop = isAndroidPwa ? '0.5rem' : '1rem';

  // Keep bottom nav hidden while Kontoinnstillinger drawer is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (showSettingsSheet) {
      document.body.classList.add(SETTINGS_SHEET_BODY_CLASS);
    } else {
      document.body.classList.remove(SETTINGS_SHEET_BODY_CLASS);
    }
    return () => {
      document.body.classList.remove(SETTINGS_SHEET_BODY_CLASS);
    };
  }, [showSettingsSheet]);

  // Let browser/device back close Kontoinnstillinger drawer before leaving page.
  useEffect(() => {
    if (!showSettingsSheet) return;
    window.history.pushState({ modal: 'employer-settings-sheet' }, '');
    const handlePopState = (e: PopStateEvent) => {
      if (e.state?.modal === 'employer-settings-sheet') return;
      setShowSettingsSheet(false);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showSettingsSheet]);

  // Ref to track if we've checked welcome splash for this user
  const hasCheckedWelcome = useRef(false);
  const hasProcessedInitialAuthRefresh = useRef(false);

  // Form fields
  const [companyName, setCompanyName] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [location, setLocation] = useState('');
  const [county, setCounty] = useState('');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');

  const { isDirty: isEditProfileDirty, markDirty: markEditProfileDirty, resetDirty: resetEditProfileDirty, setDirty: setEditProfileDirty, canApplyRemoteUpdate } = useDirtyFormGuard(false);

  const handleRestoreEditProfileDraft = useCallback((payload: { companyName: string; companyDescription: string; location: string; county: string; industry: string; website: string; }) => {
    setCompanyName(payload?.companyName || '');
    setCompanyDescription(payload?.companyDescription || '');
    setLocation(payload?.location || '');
    setCounty(payload?.county || '');
    setIndustry(payload?.industry || '');
    setWebsite(payload?.website || '');
    setEditProfileDirty(true);
  }, [setEditProfileDirty]);

  const { clearDraft: clearEmployerEditDraft } = useFormDraftPersistence({
    key: `talentfunn:draft:employer-profile:${userId}`,
    enabled: isOwnProfile && showEditDrawer,
    value: { companyName, companyDescription, location, county, industry, website },
    onRestore: handleRestoreEditProfileDraft,
  });

  useBeforeUnloadDirty(showEditDrawer && isEditProfileDirty);

  const applyProfileToState = useCallback((nextProfile: any) => {
    setProfile(nextProfile);
    setCompanyName(nextProfile?.companyName || '');
    setCompanyDescription(nextProfile?.companyDescription || '');
    setLocation(nextProfile?.location || '');
    setCounty(nextProfile?.county || '');
    setIndustry(nextProfile?.industry || '');
    setWebsite(nextProfile?.website || '');
  }, []);
  
  // Use currentUserProfile subscription when viewing own profile (synced with HomePage)
  const effectiveSubscription = isOwnProfile && currentUserProfile 
    ? currentUserProfile.subscription 
    : profile?.subscription;

  // Keep own profile form in sync with global currentUserProfile; skip when settings sheet or edit drawer is open to avoid losing unsaved edits (e.g. on visibility refresh).
  useEffect(() => {
    if (!isOwnProfile || !currentUserProfile) return;
    if (!canApplyRemoteUpdate(showSettingsSheet || showEditDrawer)) return;
    applyProfileToState(currentUserProfile);
    setCachedProfile(userId, currentUserProfile);
  }, [isOwnProfile, currentUserProfile, userId, applyProfileToState, showSettingsSheet, showEditDrawer, canApplyRemoteUpdate]);
  // Reset local profile view only when viewing context changes.
  useEffect(() => {
    const cachedProfile = getCachedProfile(userId);
    setLoading(!cachedProfile);
    if (cachedProfile && canApplyRemoteUpdate(showSettingsSheet || showEditDrawer)) {
      applyProfileToState(cachedProfile);
    } else {
      setProfile(null);
    }
    setHasPublishedGigs(null);
    hasCheckedWelcome.current = false;
    if (userId) fetchProfile();
  }, [userId, applyProfileToState, canApplyRemoteUpdate, showSettingsSheet, showEditDrawer]);

  useEffect(() => {
    // Keep data fresh on token changes without resetting local UI state.
    if (!hasProcessedInitialAuthRefresh.current) {
      hasProcessedInitialAuthRefresh.current = true;
      return;
    }
    if (!userId) return;
    if (!canApplyRemoteUpdate(showSettingsSheet || showEditDrawer)) return;
    void fetchProfile();
  }, [accessToken, userId, canApplyRemoteUpdate, showSettingsSheet, showEditDrawer]);

  // Check if employer has published any gigs (to hide "Publiser oppdrag" CTA)
  useEffect(() => {
    if (!isOwnProfile || !canCreateGig({ subscription: effectiveSubscription }) || !accessToken) {
      setHasPublishedGigs(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${API_BASE}/my-gigs`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (cancelled || !response.ok) return;
        const data = await response.json();
        setHasPublishedGigs((data.gigs?.length ?? 0) > 0);
      } catch {
        if (!cancelled) setHasPublishedGigs(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isOwnProfile, effectiveSubscription, accessToken]);

  const notifyProfileMutation = useCallback((reason: string) => {
    if (onProfileUpdate) {
      void onProfileUpdate();
    }
    window.dispatchEvent(new CustomEvent('talentfunn:profile-updated', { detail: { reason, userId, isOwnProfile } }));
    window.dispatchEvent(new CustomEvent('talentfunn:data-invalidated', { detail: { domain: 'profile', reason, userId } }));
  }, [onProfileUpdate, userId, isOwnProfile]);

  const syncProfileFromServer = useCallback(() => {
    if (isOwnProfile && currentUserProfile) {
      applyProfileToState(currentUserProfile);
      setCachedProfile(userId, currentUserProfile);
    }
  }, [isOwnProfile, currentUserProfile, userId, applyProfileToState]);

  const handleCloseSettingsSheet = useCallback(() => {
    setShowSettingsSheet(false);
    syncProfileFromServer();
  }, [syncProfileFromServer]);

  const requestCloseEditDrawer = useCallback(() => {
    if (isEditProfileDirty) {
      setShowDiscardEditConfirm(true);
      return;
    }
    setShowEditDrawer(false);
    clearEmployerEditDraft();
    resetEditProfileDirty();
    syncProfileFromServer();
  }, [isEditProfileDirty, clearEmployerEditDraft, resetEditProfileDirty, syncProfileFromServer]);

  const confirmDiscardEditDrawer = useCallback(() => {
    setShowDiscardEditConfirm(false);
    setShowEditDrawer(false);
    clearEmployerEditDraft();
    resetEditProfileDirty();
    syncProfileFromServer();
  }, [clearEmployerEditDraft, resetEditProfileDirty, syncProfileFromServer]);

  const handleOpenEditDrawer = useCallback(() => {
    setShowEditDrawer(true);
  }, []);
  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_BASE}/profile/${userId}`);

      if (response.ok) {
        const data = await response.json();
        setCachedProfile(userId, data.profile);
        if (canApplyRemoteUpdate(showSettingsSheet || showEditDrawer)) {
          applyProfileToState(data.profile);
        }
        
        // Pre-populate form fields with existing data
  
        // Show welcome splash for new employer users (only on own profile, and only once)
        if (isOwnProfile && data.profile.userType === 'employer' && !hasCheckedWelcome.current) {
          hasCheckedWelcome.current = true; // Mark as checked to prevent re-triggering
          
          const hasSeenWelcome = localStorage.getItem(`welcomeSeen_${userId}`);
          const isNewlyVerified = localStorage.getItem(`isNewlyVerifiedUser_${userId}`);
          
          // ✅ FIXED: Only show welcome screen if user just verified their email
          // This ensures it only shows ONCE per user, not every time they have an empty profile
          if (!hasSeenWelcome && isNewlyVerified) {
            // Small delay to ensure profile is fully rendered before showing splash
            setTimeout(() => {
              setShowWelcomeSplash(true);
            }, 300);
            localStorage.setItem(`welcomeSeen_${userId}`, 'true');
            // Clear the "newly verified" flag now that we've shown the welcome screen
            localStorage.removeItem(`isNewlyVerifiedUser_${userId}`);
          }
        }
      }
    } catch (error) {
      devLog('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    let shouldCloseDrawer = false;
    try {
      // Frontend profanity guard for immediate feedback (backend also enforces)
      if (containsProfanity(companyName)) {
        toast.error('Firmanavn inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }
      if (containsProfanity(companyDescription)) {
        toast.error('Firmabeskrivelse inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }
      if (containsProfanity(location)) {
        toast.error('By/Kommune inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }
      if (containsProfanity(industry)) {
        toast.error('Bransje inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }

      setSavingProfile(true);
      const response = await fetch(
        `${API_BASE}/profile`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ 
            companyName, 
            companyDescription, 
            location, 
            county, 
            industry,
            website
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        notifyProfileMutation('profile:save');
        toast.success('Profil oppdatert');
        clearEmployerEditDraft();
        resetEditProfileDirty();
        shouldCloseDrawer = true;
      } else {
        const errorData = await response.json().catch(() => null);
        devLog('Error updating profile:', errorData);
        toast.error(errorData?.error || 'Kunne ikke oppdatere profilen. Prøv igjen.');
      }
    } catch (error) {
      devLog('Error updating profile:', error);
      toast.error('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setSavingProfile(false);
      if (shouldCloseDrawer) {
        setShowEditDrawer(false);
      }
    }
  };

  const handleProfileImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Vennligst velg en bildefil');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bildet er for stort. Maksimal størrelse er 10MB.');
      return;
    }

    setUploadingProfileImage(true);

    try {
      const croppedFile = await openCrop(file, AVATAR_IMAGE_SPEC);
      const base64String = await compressImageForUpload(croppedFile, PROFILE_SHOWCASE_PRESET);

      const response = await fetch(
        `${API_BASE}/profile/upload-image`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ imageBase64: base64String }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setProfile({ ...profile, profileImage: data.profileImage });
        setShowProfileImageOptions(false);
        toast.success('Profilbilde oppdatert!');
        notifyProfileMutation('profile:image-upload');
      } else {
        toast.error(data.error || 'Kunne ikke laste opp profilbilde');
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('avbrutt')) return;
      devLog('Error uploading profile image:', error);
      toast.error('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handleDeleteProfileImage = async () => {
    setUploadingProfileImage(true);

    try {
      const response = await fetch(
        `${API_BASE}/profile/delete-image`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setProfile({ ...profile, profileImage: null });
        setShowProfileImageOptions(false);
        toast.success('Profilbilde slettet!');
        notifyProfileMutation('profile:image-delete');
      } else {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke slette profilbilde');
      }
    } catch (error) {
      devLog('Error deleting profile image:', error);
      toast.error('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setUploadingProfileImage(false);
    }
  };

  // Fetch blocked users
  const fetchBlockedUsers = async () => {
    if (!accessToken) return;
    
    setLoadingBlockedUsers(true);
    try {
      const response = await fetch(
        `${API_BASE}/blocked`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBlockedUsers(data.blockedUsers || []);
      }
    } catch (error) {
      devLog('Error fetching blocked users:', error);
    } finally {
      setLoadingBlockedUsers(false);
    }
  };

  // Unblock user
  const handleUnblockUser = async (userId: string) => {
    if (!accessToken) return;

    try {
      setUnblockingUserId(userId);
      const response = await fetch(
        `${API_BASE}/unblock`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (response.ok) {
        toast.success('Bruker fjernet fra blokkerte');
        // Remove from local state
        setBlockedUsers(blockedUsers.filter((user) => user.id !== userId));
      } else {
        toast.error('Kunne ikke fjerne blokkering');
      }
    } catch (error) {
      devLog('Error unblocking user:', error);
      toast.error('Noe gikk galt');
    } finally {
      setUnblockingUserId(null);
    }
  };

  // Don't block the whole profile on secondary checks like my-gigs.
  if (loading) {
    return (
      <div className="fixed inset-0 z-10 flex flex-col items-center justify-center text-center bg-transparent">
        <Loader size="lg" className="text-purple-600" />
        <p className="mt-4">Laster profil...</p>
      </div>
    );
  }

  const INDUSTRIES = [
    'Bygg og anlegg',
    'IT og teknologi',
    'Helse og omsorg',
    'Handel og service',
    'Industri og produksjon',
    'Transport og logistikk',
    'Hotell og restaurant',
    'Utdanning',
    'Finans og forsikring',
    'Eiendom',
    'Annet'
  ];

  const editDrawerHeader = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={requestCloseEditDrawer}
          className="xl:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Tilbake"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h2 className="text-lg xl:text-xl font-bold text-purple-900 xl:font-semibold xl:text-gray-900 truncate">Rediger profil</h2>
      </div>
      <button
        onClick={requestCloseEditDrawer}
        className="hidden xl:flex p-2 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Lukk"
      >
        <X className="w-6 h-6 text-gray-600" />
      </button>
    </>
  );

  const editDrawerEl = (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className={`min-h-screen bg-white xl:bg-transparent xl:fixed xl:inset-0 xl:talentfunn-modal-overlay xl:backdrop-blur-md flex items-start xl:items-center justify-center p-0 xl:p-4 xl:pt-4 xl:pb-4 ${(isIosPwa || isAndroid) ? 'fixed inset-0 flex-col overflow-hidden' : 'xl:overflow-y-auto'}`}
          style={{ zIndex: 'var(--z-modal)' }}
        >
          {/* iOS PWA: fixed header + spacer (samme mønster som CreateGigModal) */}
          {isIosPwa && (
            <>
              <div
                className="xl:hidden fixed left-0 right-0 top-0 z-20 flex-shrink-0 min-h-[3.5rem] bg-white border-b border-gray-200 px-4 pb-3 flex items-center justify-between rounded-none"
                style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)' }}
              >
                {editDrawerHeader}
              </div>
              <div
                className="xl:hidden flex-shrink-0 edit-drawer-spacer"
                aria-hidden="true"
              />
            </>
          )}
          {/* Android (nettleser + PWA): fast header + spacer – match user profile edit drawer */}
          {isAndroid && (
            <>
              <div
                className="xl:hidden fixed left-0 right-0 top-0 z-20 flex-shrink-0 min-h-[3.5rem] bg-white border-b border-gray-200 px-4 pb-3 flex items-center justify-between rounded-none"
                style={{ paddingTop: `calc(env(safe-area-inset-top, 0px) + ${androidHeaderPadTop})` }}
              >
                {editDrawerHeader}
              </div>
              <div
                className="xl:hidden flex-shrink-0"
                style={{ height: `calc(env(safe-area-inset-top, 0px) + ${androidHeaderPadTop} + 3.5rem + 0.75rem)` }}
                aria-hidden="true"
              />
            </>
          )}
          <motion.div
            initial={{ y: 20, opacity: 0.98 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`edit-drawer-shell bg-white w-full min-h-screen xl:min-h-0 rounded-none xl:rounded-2xl max-w-none xl:max-w-2xl xl:max-h-[85vh] shadow-none xl:shadow-2xl xl:overflow-y-auto xl:pt-0 ${(isIosPwa || isAndroid) ? 'flex-1 min-h-0 max-h-[100dvh] overflow-y-auto flex flex-col' : ''}`}
          >
            {/* Header – på iOS PWA vises headeren i overlay over; ellers her */}
            {!(isIosPwa || isAndroid) && (
              <div className="sub-page-header sticky top-0 z-20 flex-shrink-0 min-h-[3.5rem] xl:min-h-0 bg-white border-b border-gray-200 px-4 pt-4 pb-3 xl:px-6 xl:pt-4 xl:pb-4 flex items-center justify-between rounded-none xl:rounded-t-2xl">
                {editDrawerHeader}
              </div>
            )}
            {/* iOS PWA: samme som CreateGigModal – wrapper med ios-scroll mellom inner og content */}
            {(() => {
              const Wrapper = (isIosPwa || isAndroid) ? 'div' : Fragment;
              const wrapperProps = (isIosPwa || isAndroid) ? { className: 'overflow-x-hidden ios-scroll flex-1 min-h-0' } : {};
              return (
                <Wrapper {...wrapperProps}>
            <div className="edit-profile-drawer-content edit-drawer-content-bottom px-6 py-6 space-y-6 pb-8 xl:pb-8">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bedriftsnavn
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => { setCompanyName(e.target.value); markEditProfileDirty(); }}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Bedriftens navn"
                />
              </div>

              {/* County */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fylke
                </label>
                <select
                  value={county}
                  onChange={(e) => { setCounty(e.target.value); markEditProfileDirty(); }}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="">Velg fylke</option>
                  {NORWEGIAN_COUNTIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  By/Kommune
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => { setLocation(e.target.value); markEditProfileDirty(); }}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="F.eks. Oslo, Bergen, Trondheim..."
                />
              </div>

              {/* Company Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Om bedriften
                </label>
                <textarea
                  value={companyDescription}
                  onChange={(e) => { setCompanyDescription(e.target.value); markEditProfileDirty(); }}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={4}
                  placeholder="Beskriv bedriften din, hva dere gjør, og hva slags talenter dere søker..."
                />
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bransje
                </label>
                <select
                  value={industry}
                  onChange={(e) => { setIndustry(e.target.value); markEditProfileDirty(); }}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="">Velg bransje</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nettside
                </label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => { setWebsite(e.target.value); markEditProfileDirty(); }}
                  className="w-full border-2 border-purple-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://www.bedrift.no"
                />
              </div>
              
              {/* Action Buttons */}
              <div className="pt-6 border-t-2 border-purple-200">
                <div className="flex gap-2 pb-safe">
                  <button
                    onClick={requestCloseEditDrawer}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={() => handleSaveProfile()}
                    disabled={savingProfile}
                    className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingProfile ? 'Lagrer endringer...' : 'Lagre endringer'}
                  </button>
                </div>
              </div>
            </div>
                </Wrapper>
              );
            })()}
          </motion.div>
        </motion.div>
      </AnimatePresence>
  );
  if (showEditDrawer && isOwnProfile && !isDesktop) return editDrawerEl;

  return (
    <>
      {showEditDrawer && isOwnProfile && isDesktop && ReactDOM.createPortal(editDrawerEl, document.body)}
    <div className="min-h-screen bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50">
        {/* Wrapper - fullscreen padding on mobile, modal-style on desktop */}
        <PageShell className="pt-page-safe-sm xl:max-w-4xl xl:mx-auto xl:px-4 xl:px-8 xl:pt-5 xl:pb-24">
      {/* Content container - fullscreen on mobile, rounded modal on desktop */}
      <div className="bg-white xl:rounded-3xl xl:shadow-lg overflow-hidden xl:border-2 xl:border-purple-100 relative">
        
        {/* Settings icon for own profile - top right */}
        {isOwnProfile && accessToken && !showWelcomeSplash && !showEditDrawer && !showSettingsSheet && (
          <button
            onClick={() => setShowSettingsSheet(true)}
            className="absolute top-4 right-4 z-50 bg-white bg-opacity-90 p-2.5 rounded-full shadow-lg hover:bg-opacity-100 transition-all"
          >
            <SettingsIcon className="w-5 h-5 text-gray-700" />
          </button>
        )}
        
        {/* Cover gradient - Compact */}
        <div className="h-24 xl:h-32 bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 xl:rounded-t-3xl" />

        <div className="px-4 sm:px-8 pb-6 sm:pb-8">
          {/* Avatar and basic info - Centered on mobile */}
          <div className="flex flex-col mb-[-10px]">
            <div className="flex flex-col items-center sm:items-start sm:flex-row sm:gap-6 -mt-16 sm:-mt-12 mb-4">
              <div className="relative group">
                {profile?.profileImage ? (
                  <img
                    src={profile.profileImage}
                    alt={profile.companyName || profile.name}
                    className="w-28 h-28 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-xl flex-shrink-0 object-cover bg-white"
                  />
                ) : (
                  <div className="w-28 h-28 sm:w-32 sm:h-32 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center border-4 border-white shadow-xl flex-shrink-0">
                    <Building2 className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
                  </div>
                )}
                {isOwnProfile && accessToken && (
                  <button
                    onClick={() => setShowProfileImageOptions(!showProfileImageOptions)}
                    className="absolute bottom-0 right-0 bg-blue-600 text-white p-2.5 rounded-full shadow-lg hover:bg-blue-700 transition-all"
                    disabled={uploadingProfileImage}
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                )}
                {showProfileImageOptions && isOwnProfile && (
                  <div className="absolute left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 top-full mt-2 bg-white rounded-xl shadow-xl border-2 border-blue-200 p-3 z-10 min-w-[200px]">
                    <label className="flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg cursor-pointer transition-all">
                      <Upload className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-700">
                        {profile?.profileImage ? 'Endre bilde' : 'Last opp bilde'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfileImageUpload}
                        className="hidden"
                        disabled={uploadingProfileImage}
                      />
                    </label>
                    {profile?.profileImage && (
                      <button
                        onClick={handleDeleteProfileImage}
                        disabled={uploadingProfileImage}
                        className="flex items-center gap-2 px-4 py-2 hover:bg-red-50 rounded-lg transition-all w-full text-left"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                        <span className="text-sm text-red-600">Slett bilde</span>
                      </button>
                    )}
                    {uploadingProfileImage && (
                      <div className="px-4 py-2 text-sm text-gray-500">
                        Laster opp...
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Profile info - centered on mobile */}
              <div className="text-center sm:text-left sm:pb-2 sm:pt-16 mt-2 sm:mt-0">
                <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-2">
                  {profile?.companyName || profile?.name || 'Arbeidsgiver'}
                </h1>
                <>
                  {(profile?.location || profile?.county) && (
                    <div className="flex items-center justify-center sm:justify-start gap-1.5 text-gray-600 mb-1">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm sm:text-base">
                        {[profile?.location, profile?.county].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {profile?.industry && (
                    <div className="flex items-center justify-center sm:justify-start gap-1.5 text-gray-600">
                      <Building2 className="w-5 h-5" />
                      <span className="text-base sm:text-lg">{profile.industry}</span>
                    </div>
                  )}
                </>
            </div>
            
            {/* Action buttons - Native mobile style */}
            {/* Action buttons removed - now use Settings modal */}
          </div>

          {/* Company Description - Part of unified container */}
          <div className="py-5 border-b border-gray-200">
            <h3 className="mb-3 text-gray-900 font-semibold text-base">
              Om bedriften
            </h3>
            <LinkifiedText
              text={profile?.companyDescription || profile?.bio || 'Ingen beskrivelse ennå'}
              className="text-gray-600 text-lg"
            />
          </div>

          {/* ✅ Call-to-action banner for Employer Plus users (hidden after they have published at least one gig) */}
          {isOwnProfile && effectiveSubscription === 'employer_plus' && hasPublishedGigs === false && (
            <div className="py-5 border-b border-gray-200">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl p-6 shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 p-3 rounded-xl flex-shrink-0">
                    <Rocket className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">Klar til å finne talent?</h3>
                    <p className="text-blue-100 mb-4">
                      Som Arbeidsgiver Pluss-medlem kan du nå publisere oppdrag og nå tusenvis av dyktige talenter.
                    </p>
                    <button
                      onClick={() => onCreateGig ? onCreateGig() : onNavigate?.('gigs')}
                      className="bg-white text-blue-600 px-6 py-2.5 rounded-xl font-semibold hover:shadow-lg transition-all inline-flex items-center gap-2"
                    >
                      <Briefcase className="w-5 h-5" />
                      <span>Publiser oppdrag</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Industry removed from inline view - now in modal */}

          {/* Website - Part of unified container */}
          <div className="py-6">
            <h3 className="mb-3 flex items-center gap-2 text-purple-900">
              <Globe className="w-5 h-5" />
              Nettside
            </h3>
            <p className="text-gray-600 text-lg">
              {profile?.website ? (
                <a 
                  href={profile.website.startsWith('http://') || profile.website.startsWith('https://') 
                    ? profile.website 
                    : `https://${profile.website}`
                  } 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {profile.website}
                </a>
              ) : (
                'Ingen nettside lagt til'
              )}
            </p>
          </div>

          {/* Quick Actions - Only shown for own profile */}
          {isOwnProfile && (
            <div className="pt-4 space-y-3">
              {/* View Favorites */}
              <button
                onClick={() => {
                  if (!canUseFavorites({ subscription: effectiveSubscription })) {
                    toast.warning(
                      'Oppgrader til Plus',
                      'Favoritter er kun tilgjengelig for Plus-brukere. Oppgrader for å få tilgang!'
                    );
                    return;
                  }
                  if (onViewFavorites) onViewFavorites();
                }}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all text-left ${
                  !canUseFavorites({ subscription: effectiveSubscription })
                    ? 'bg-gray-50 opacity-60 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-purple-50 to-pink-50 hover:shadow-md border-2 border-purple-100'
                }`}
              >
                <Heart className="w-5 h-5 text-purple-700" />
                <div className="flex-1 flex items-center justify-between">
                  <span className="font-medium text-purple-900">Mine favoritter</span>
                  {!canUseFavorites({ subscription: effectiveSubscription }) && (
                    <Lock className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </button>

              {/* View Saved Searches */}
              <button
                onClick={() => {
                  if (!canUseSavedSearches({ subscription: effectiveSubscription })) {
                    toast.warning(
                      'Oppgrader til Plus',
                      'Lagrede søk er kun tilgjengelig for Plus-brukere. Oppgrader for å få tilgang!'
                    );
                    return;
                  }
                  if (onViewSavedSearches) onViewSavedSearches();
                }}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all text-left ${
                  !canUseSavedSearches({ subscription: effectiveSubscription })
                    ? 'bg-gray-50 opacity-60 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-purple-50 to-pink-50 hover:shadow-md border-2 border-purple-100'
                }`}
              >
                <BookmarkPlus className="w-5 h-5 text-purple-700" />
                <div className="flex-1 flex items-center justify-between">
                  <span className="font-medium text-purple-900">Lagrede søk</span>
                  {!canUseSavedSearches({ subscription: effectiveSubscription }) && (
                    <Lock className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </button>
              
              {/* Mine oppdrag (for employer_plus users) */}
              {canCreateGig({ subscription: effectiveSubscription }) && onNavigate && (
                <button
                  onClick={() => {
                    setShowSettingsSheet(false);
                    onNavigate('my-gigs');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 bg-gradient-to-r from-purple-50 to-pink-50 hover:shadow-md border-2 border-purple-100 rounded-xl transition-all text-left"
                >
                  <Briefcase className="w-5 h-5 text-purple-700" />
                  <span className="font-medium text-purple-900">Mine oppdrag</span>
                </button>
              )}

              {/* Aktuelle oppdrag (plus / employer / employer_plus) */}
              {canShowGigInterest({ subscription: effectiveSubscription }) && onNavigate && (
                <button
                  onClick={() => {
                    setShowSettingsSheet(false);
                    onNavigate('my-interests');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 bg-gradient-to-r from-purple-50 to-pink-50 hover:shadow-md border-2 border-purple-100 rounded-xl transition-all text-left"
                >
                  <NotebookText className="w-5 h-5 text-purple-700" />
                  <span className="font-medium text-purple-900">Aktuelle oppdrag</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info card - only for own profile (employer viewing their own page) */}
      {isOwnProfile && (
        <div className="mx-4 mt-6 mb-6 bg-gradient-to-br from-blue-50 to-teal-50 rounded-3xl shadow-md p-8 border-2 border-blue-100 xl:mx-6 xl:mt-8">
          <h3 className="text-2xl bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent pb-1 mb-4">
            Som arbeidsgiver på Talentfunn
          </h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>Søk etter talenter basert på ferdigheter og lokasjon</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>Se portfolioer og tidligere prosjekter</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>Kontakt talenter direkte med meldinger (krever abonnement)</span>
            </li>
          </ul>
        </div>
      )}

      </div>

      {/* Welcome Splash for New Employers */}
      {showWelcomeSplash && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowWelcomeSplash(false)}
            className="fixed inset-0 bg-black/20 backdrop-blur-md"
            style={{ zIndex: 'var(--z-overlay)' }}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe pointer-events-none overflow-y-auto" style={{ zIndex: 'var(--z-overlay)' }}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-4 sm:p-8 xl:p-12 relative overflow-hidden my-auto max-h-[65vh] sm:max-h-[75vh] overflow-y-auto pointer-events-auto">
              {/* Content */}
              <div className="relative">
                {/* Icon */}
                <div className="flex justify-center mb-3 sm:mb-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-500 via-teal-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                </div>
                
                {/* Title */}
                <h2 className="text-xl sm:text-2xl xl:text-3xl bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent pb-1 mb-1 sm:mb-2 text-center leading-tight break-words">
                  Velkommen til Talentfunn! <span className="inline-block align-middle">🎉</span>
                </h2>
                
                {/* Description */}
                <p className="text-sm sm:text-base text-gray-600 text-center mb-4 sm:mb-6">
                  Vi er glade for å ha deg her som arbeidsgiver!
                </p>
                
                {/* Steps */}
                <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                  <div className="flex items-start gap-2 bg-blue-50 p-2 sm:p-3 rounded-2xl border-2 border-blue-200">
                    <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm">
                      1
                    </div>
                    <div>
                      <h3 className="text-blue-900 text-xs sm:text-sm">Fullfør profilen din</h3>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 bg-teal-50 p-2 sm:p-3 rounded-2xl border-2 border-teal-200">
                    <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm">
                      2
                    </div>
                    <div>
                      <h3 className="text-teal-900 text-xs sm:text-sm">Søk etter talenter</h3>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2 bg-cyan-50 p-2 sm:p-3 rounded-2xl border-2 border-cyan-200">
                    <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm">
                      3
                    </div>
                    <div>
                      <h3 className="text-cyan-900 text-xs sm:text-sm">Velg abonnement for å kontakte talenter</h3>
                    </div>
                  </div>
                </div>
                
                {/* CTA Button */}
                <button
                  onClick={() => setShowWelcomeSplash(false)}
                  className="w-full bg-gradient-to-r from-blue-500 via-teal-500 to-cyan-500 text-white py-3 rounded-2xl hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  Kom i gang!
                  <Rocket className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {cropModal}

      <ConfirmDialog
        isOpen={showDiscardEditConfirm}
        onClose={() => setShowDiscardEditConfirm(false)}
        onConfirm={confirmDiscardEditDrawer}
        title="Forkaste endringer?"
        message="Du har ulagrede endringer i Rediger profil. Vil du forkaste disse?"
        confirmText="Forkast"
        cancelText="Fortsett redigering"
        variant="warning"
      />

      {/* Settings Sheet - Native mobile drawer */}
      <AnimatePresence>
      {showSettingsSheet && isOwnProfile && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSettingsSheet(false)}
            className="fixed inset-0 bg-black/20 backdrop-blur-md"
            style={{ zIndex: 'var(--z-overlay)' }}
          />
          
          {/* Sheet - bottom drawer on mobile, centered modal on desktop */}
          <motion.div 
            initial={{ y: "100%", opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: "100%", opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed bottom-[-1px] left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 max-h-[70vh] overflow-y-auto ios-scroll xl:bottom-auto xl:top-1/2 xl:left-1/2 xl:-translate-x-1/2 xl:-translate-y-1/2 xl:rounded-2xl xl:max-w-md xl:w-full border-t-2 border-gray-200 xl:border-0"
          >
            {/* Handle bar - Mobile only */}
            <div className="flex justify-center pt-3 pb-2 xl:hidden">
              <div className="w-12 h-1 bg-gray-300 rounded-full" />
            </div>
            
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Innstillinger</h2>
                <button
                  onClick={() => setShowSettingsSheet(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>
            </div>
            
            {/* Settings options */}
            <div className="px-6 py-4">
              <div className="space-y-2">
                {/* Edit Profile */}
                <button
                  onClick={() => {
                    handleCloseSettingsSheet();
                    handleOpenEditDrawer();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-100 rounded-xl transition-all text-left"
                >
                  <Edit2 className="w-5 h-5 text-gray-700" />
                  <span className="font-medium text-gray-900">Rediger profil</span>
                </button>
                
                {/* Blocked Users */}
                <button
                  onClick={() => {
                    setShowSettingsSheet(false);
                    setShowBlockedUsers(true);
                    fetchBlockedUsers();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-100 rounded-xl transition-all text-left"
                >
                  <Ban className="w-5 h-5 text-gray-700" />
                  <span className="font-medium text-gray-900">Blokkerte brukere</span>
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
      </AnimatePresence>

      {/* Edit profile is handled as full-page takeover above */}
        
        {/* Blocked Users Modal */}
      {showBlockedUsers && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-modal)' }}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent pb-1">
                Blokkerte brukere
              </h3>
              <button
                onClick={() => setShowBlockedUsers(false)}
                className="p-2 hover:bg-blue-50 rounded-full transition-all"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {loadingBlockedUsers ? (
                <div className="text-center py-8">
                  <Loader size="md" className="text-blue-600" />
                  <p className="mt-2 text-gray-600">Laster...</p>
                </div>
              ) : blockedUsers.length === 0 ? (
                <div className="text-center py-8">
                  <Ban className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">Ingen blokkerte brukere</p>
                </div>
              ) : (
                blockedUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      {user.profileImage ? (
                        <img
                          src={user.profileImage}
                          alt={user.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        {user.location && (
                          <p className="text-sm text-gray-500">{user.location}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnblockUser(user.id)}
                      disabled={unblockingUserId === user.id}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {unblockingUserId === user.id ? 'Opphever...' : 'Opphev'}
                    </button>
                  </div>
                ))
              )}
            </div>
            
            <button
              onClick={() => setShowBlockedUsers(false)}
              className="w-full mt-4 px-6 py-3 border-2 border-blue-200 text-blue-600 rounded-xl hover:bg-blue-50 transition-all"
            >
              Lukk
            </button>
          </div>
        </div>,
        document.body
      )}
      </PageShell>
    </div>
    </>
  );
}
